import memeService from "./../MemeService";
import notificationService from "./../NotificationService";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

const PreloadSettingsService = () => {
    const { t } = useTranslation("preloadErrors");

    const [browserSourceInterval, setBrowserSourceInterval] = useState(10000);
    const [browserSourceTimeOut, setBrowserSourceTimeOut] = useState(60000);
    const [browserSourceNotifySound, setBrowsersourceNotifySound] = useState(0);
    const [broadcasterThemeStyle, setBroadcasterThemStyle] = useState(0);
    const [manualApproval, setManualApproval] = useState(false);
    const [chatReminderInterval, setChatReminderInterval] = useState(60);
    const [storageLimit, setStorageLimit] = useState(20);
    const [uploadLimit, setUploadLimit] = useState(200);

    function getSettings() {
        return {
            browserSourceInterval,
            browserSourceTimeOut,
            browserSourceNotifySound,
            broadcasterThemeStyle,
            manualApproval,
            chatReminderInterval,
            storageLimit,
            uploadLimit,
        };
    }

    function updateSetting(settingName, value) {
        switch (settingName) {
            case "browserSourceInterval":
                setBrowserSourceInterval(value);
                break;
            case "browserSourceTimeOut":
                setBrowserSourceTimeOut(value);
                break;
            case "browserSourceNotifySound":
                setBrowsersourceNotifySound(value);
                break;
            case "broadcasterThemeStyle":
                setBroadcasterThemStyle(value);
                break;
            case "manualApproval":
                setManualApproval(value);
                break;
            case "chatReminderInterval":
                setChatReminderInterval(value);
                break;
            default:
                console.warn(
                    `PreloadSettingsService -> updateSetting : Setting name "${settingName}" not recognized.`,
                );
        }
    }

    const verifySettings = (updateSettings) => {
        let invalidFields = [];

        // Check if the properties exist before checking their validity
        if (
            "browserSourceInterval" in updateSettings &&
            isNaN(updateSettings.browserSourceInterval)
        ) {
            invalidFields.push("browserSourceInterval");
        }
        if (
            "browserSourceNotifySound" in updateSettings &&
            isNaN(updateSettings.browserSourceNotifySound)
        ) {
            invalidFields.push("browserSourceNotifySound");
        }
        if (
            "broadcasterThemeStyle" in updateSettings &&
            isNaN(updateSettings.broadcasterThemeStyle)
        ) {
            invalidFields.push("broadcasterThemeStyle");
        }
        if (
            "browserSourceTimeOut" in updateSettings &&
            isNaN(updateSettings.browserSourceTimeOut)
        ) {
            invalidFields.push("browserSourceTimeOut");
        }
        if (
            "chatReminderInterval" in updateSettings &&
            isNaN(updateSettings.chatReminderInterval)
        ) {
            invalidFields.push("chatReminderInterval");
        }
        if (
            "manualApproval" in updateSettings &&
            typeof updateSettings.manualApproval !== "boolean"
        ) {
            invalidFields.push("manualApproval");
        }

        // If there are invalid fields, throw an error with details
        if (invalidFields.length > 0) {
            console.error("verifySettings object : ", updateSettings);
            throw new Error(
                `verifySettings -> Invalid settings: ${invalidFields.join(", ")}`,
            );
        }
    };
    const updateUserSettings = async (updatedSettings) => {
        // Check if any setting is defined in updatedSettings
        const isAnySettingDefined = Object.keys(updatedSettings).some(
            (key) =>
                updatedSettings[key] !== undefined &&
                updatedSettings[key] !== null,
        );

        if (!isAnySettingDefined) {
            return console.warn(
                "No settings were provided for PreloadSettingsService -> updateUserSettings.",
                "Example : updateUserSettings({ browserSourceInterval: 30 });",
                "Options : browserSourceInterval, browserSourceNotifySound, broadcasterThemeStyle, manualApproval, chatReminderInterval",
            );
        }

        try {
            verifySettings(updatedSettings);

            const settings =
                await memeService.updateBroadcasterSettingsAll(updatedSettings);

            const isSettingsMatch = Object.keys(updatedSettings).every(
                (key) => {
                    return (
                        Object.hasOwnProperty.call(settings, key) &&
                        settings[key] === updatedSettings[key]
                    );
                },
            );

            if (!isSettingsMatch) {
                console.error("Got : ", settings);
                console.error("Tried to save : ", updatedSettings);
                throw new Error("Settings missmatch!");
            }

            updateSetting(
                "browserSourceInterval",
                parseInt(settings.browserSourceInterval),
            );
            updateSetting(
                "browserSourceTimeOut",
                parseInt(settings.browserSourceTimeOut),
            );
            updateSetting(
                "browserSourceNotifySound",
                parseInt(settings.browserSourceNotifySound),
            );
            updateSetting(
                "broadcasterThemeStyle",
                parseInt(settings.broadcasterThemeStyle),
            );
            updateSetting(
                "chatReminderInterval",
                parseInt(settings.chatReminderInterval),
            );
            updateSetting("manualApproval", settings.manualApproval);

            return settings;
        } catch (error) {
            notificationService.setTitle(t("settingsErrorWhileUpdatingTitle"));
            notificationService.setMessage(t("settingsErrorWhileUpdating"));
            console.error(
                "PreloadSettingsService -> updateUserSettings: ",
                error,
            );
        }
    };

    const loadUserSettings = useCallback(async () => {
        let settings = await memeService.getBroadcasterSettings();

        try {
            setBrowserSourceInterval(settings.browserSourceInterval);
            setBrowserSourceTimeOut(settings.browserSourceTimeOut);
            setBrowsersourceNotifySound(settings.browserSourceNotifySound);
            setBroadcasterThemStyle(settings.broadcasterThemeStyle);
            setManualApproval(settings.manualApproval);
            setChatReminderInterval(settings.chatReminderInterval);
            setStorageLimit(settings.storageLimit);
            setUploadLimit(settings.uploadLimit);
        } catch (error) {
            notificationService.setMessage(t("settingsErrorLoading"));
            console.error("PreloadSettingsService -> loadUserSettings:", error);
        }

        return settings;
    }, [t]);

    return {
        getSettings,
        loadUserSettings,
        updateUserSettings,
    };
};

export default PreloadSettingsService;
